import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Inbox, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * AnfragenFetcherButton – Knopf "Postfach prüfen" (Dashboard, Kontakte)
 * 07.10.2026 (Ralph, Variante B): löst dieselbe Prüfung aus wie "Postfach prüfen" in den
 * Mail-Anfragen (POST /module-mail-inbox/scan – gleiche Postfächer, gleiche Ablage
 * module_mail_inbox). Der alte Abruf (/anfragen/fetch -> Ablage "anfragen") wird hier
 * nicht mehr aufgerufen.
 */
const AnfragenFetcherButton = ({ onFetched, size = "sm", variant = "outline", className = "" }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  useEffect(() => { loadStatus(); }, []);

  // Letzte Prüfung = jüngste Prüfung über alle aktiven Postfächer der Mail-Anfragen
  const loadStatus = async () => {
    try {
      const res = await api.get("/module-mail-inbox/accounts");
      const zeiten = (res.data || []).filter((a) => a.active && a.last_scan_at).map((a) => a.last_scan_at).sort();
      setLastFetch(zeiten.length ? zeiten[zeiten.length - 1] : null);
    } catch {
      /* silent */
    }
  };

  const handleFetch = async () => {
    setLoading(true);
    try {
      const res = await api.post("/module-mail-inbox/scan?weeks=6&max_count=30");
      const { found = 0, per_account = [] } = res.data || {};
      const fehlerhaft = per_account.filter((a) => a.error);
      if (found > 0) {
        toast.success(`${found} ${found === 1 ? "neue Anfrage" : "neue Anfragen"}`, {
          action: { label: "Anzeigen", onClick: () => navigate("/module/mail-inbox") },
          duration: 10000,
        });
      } else if (fehlerhaft.length === 0) {
        toast.info("Keine neuen Anfragen");
      }
      fehlerhaft.forEach((a) => {
        toast.warning(`Postfach „${a.label || a.username}“ konnte nicht geprüft werden. Bitte in den Mail-Anfragen die Zugangsdaten prüfen.`);
      });
      onFetched?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (!err?.response) {
        toast.error("Keine Verbindung zum Server. Bitte gleich noch einmal versuchen.");
      } else if (typeof detail === "string" && detail.includes("Kein aktives IMAP-Postfach")) {
        toast.error("Es ist kein Postfach eingerichtet. Bitte in den Mail-Anfragen ein Postfach anlegen.");
      } else {
        toast.error("Postfach konnte nicht geprüft werden. Bitte später noch einmal versuchen.");
      }
    } finally {
      setLoading(false);
      loadStatus();
    }
  };

  const formatLast = (iso) => {
    if (!iso) return "noch nie";
    try {
      const d = new Date(iso);
      const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
      if (diffMin < 1) return "gerade eben";
      if (diffMin < 60) return `vor ${diffMin} Min`;
      const diffH = Math.floor(diffMin / 60);
      if (diffH < 24) return `vor ${diffH} Std`;
      return d.toLocaleDateString("de-DE") + " " + d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    } catch { return "-"; }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Button
        onClick={handleFetch}
        disabled={loading}
        size={size}
        variant={variant}
        data-testid="btn-fetch-anfragen"
        className="gap-2 bg-background text-foreground border-border"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Inbox className="w-4 h-4" />}
        {loading ? "wird geprüft …" : "Postfach prüfen"}
      </Button>
      <span className="text-xs text-muted-foreground" data-testid="anfragen-fetcher-last">
        Letzte Prüfung: {formatLast(lastFetch)}
      </span>
    </div>
  );
};

export { AnfragenFetcherButton };
